<template>
  <section class="bm-details" :data-testid="`batch-details-${batch.id}`">
    <div class="bm-details__head">
      <div class="bm-details__label">Name and description</div>
      <button
        v-if="batch.detailsEditable && !editing"
        type="button"
        class="bm-btn bm-btn--sm"
        :data-testid="`batch-details-edit-${batch.id}`"
        @click="startEdit"
      >
        <i class="material-icons bm-btn__icon" aria-hidden="true">edit</i>
        {{ hasText ? 'Edit' : 'Add a name or description' }}
      </button>
    </div>

    <!-- READ VIEW. Text interpolation only: these are plain text. -->
    <template v-if="!editing">
      <div
        v-if="batch.displayName"
        class="bm-details__name"
        :data-testid="`batch-details-name-${batch.id}`"
      >{{ batch.displayName }}</div>
      <div
        v-if="batch.description"
        class="bm-details__desc"
        :data-testid="`batch-details-description-${batch.id}`"
      >{{ batch.description }}</div>
      <p v-if="!hasText" class="bm-details__quiet">
        No name or description.
      </p>
    </template>

    <!-- EDIT FORM -->
    <form v-else class="bm-details__form" @submit.prevent="save">
      <label class="bm-details__field-label" :for="`batch-details-name-input-${batch.id}`">
        Name
      </label>
      <input
        :id="`batch-details-name-input-${batch.id}`"
        v-model="draftName"
        type="text"
        class="bm-details__input"
        :maxlength="nameMax"
        placeholder="e.g. Fall staff order"
        :data-testid="`batch-details-name-input-${batch.id}`"
      />
      <div class="bm-details__count">{{ draftName.length }} / {{ nameMax }}</div>

      <label class="bm-details__field-label" :for="`batch-details-description-input-${batch.id}`">
        Description
      </label>
      <textarea
        :id="`batch-details-description-input-${batch.id}`"
        v-model="draftDescription"
        class="bm-details__input bm-details__textarea"
        rows="4"
        :maxlength="descriptionMax"
        placeholder="Anything the back office should know about this batch"
        :data-testid="`batch-details-description-input-${batch.id}`"
      />
      <div class="bm-details__count">{{ draftDescription.length }} / {{ descriptionMax }}</div>

      <p
        v-if="error"
        class="bm-details__error"
        role="alert"
        :data-testid="`batch-details-error-${batch.id}`"
      >{{ error }}</p>

      <div class="bm-details__actions">
        <!--
          type="button" + @click rather than relying on submit, so the click
          is the same code path everywhere; Enter in the name field still
          submits through the form's @submit.
        -->
        <button
          type="button"
          class="bm-btn bm-btn--sm bm-btn--primary"
          :disabled="saving"
          :aria-busy="String(saving)"
          :data-testid="`batch-details-save-${batch.id}`"
          @click="save"
        >
          <template v-if="saving">Saving&hellip;</template>
          <template v-else>Save</template>
        </button>
        <button
          type="button"
          class="bm-btn bm-btn--sm"
          :disabled="saving"
          :data-testid="`batch-details-cancel-${batch.id}`"
          @click="cancel"
        >
          Cancel
        </button>
      </div>
    </form>

    <!--
      WHAT THE BACK OFFICE HAS. The batch number is the one it ACCEPTED, from
      the server; a batch registered before this field existed, or on a back
      office that already held that number, has none -- and says so.
    -->
    <p class="bm-details__backoffice" :data-testid="`batch-details-external-id-${batch.id}`">
      <template v-if="batch.backOfficeExternalId">
        Back-office batch number: <b>{{ batch.backOfficeExternalId }}</b>
      </template>
      <template v-else-if="batch.backOfficeRegistered">
        Registered with the back office without a batch number.
      </template>
      <template v-else>
        Not registered with the back office yet.
      </template>
    </p>

    <div v-if="batch.backOfficeNotes" class="bm-details__notes">
      <div class="bm-details__field-label">
        {{ batch.detailsEditable ? 'Back-office notes, sent when this batch is released' : 'Back-office notes' }}
      </div>
      <div
        class="bm-details__desc"
        :data-testid="`batch-details-notes-preview-${batch.id}`"
      >{{ batch.backOfficeNotes }}</div>
    </div>
    <p v-else-if="batch.detailsEditable" class="bm-details__quiet">
      The name and description are sent to the back office as the batch notes
      when this batch is released.
    </p>
  </section>
</template>

<script>
/**
 * A batch's name and description (#475), edited inline in the expanded row.
 *
 * Card: #475 Store batches: distributor can name a batch and add a
 * description (sent to back office) https://trello.com/c/DbzU61aR
 *
 * Owns its one request (the save) and hands the server's re-read batch up
 * through `updated`, which the panel swaps in with `replaceBatch` -- the same
 * shape every other row action returns.
 *
 * Editable only while `batch.detailsEditable` (served): the back office takes
 * the notes on its CLOSE, at release, so after that nothing would carry an
 * edit. Released batches show the values read-only with no control, per the
 * row's "released is inert" rule.
 *
 * PLAIN TEXT: every value is rendered with `{{ }}`. No v-html, anywhere.
 */
import { updateBatchDetails } from "@/services/batchManagementService";

// Mirrors services/batch_naming.py. The server is the authority (it 400s
// anything longer); these only stop the input before the round-trip.
const NAME_MAX = 200;
const DESCRIPTION_MAX = 2000;

export default {
  name: "BatchDetails",
  props: {
    batch: { type: Object, required: true },
  },
  emits: ["updated"],
  data() {
    return {
      editing: false,
      saving: false,
      error: "",
      draftName: "",
      draftDescription: "",
      nameMax: NAME_MAX,
      descriptionMax: DESCRIPTION_MAX,
    };
  },
  computed: {
    hasText() {
      return Boolean(this.batch.displayName || this.batch.description);
    },
  },
  methods: {
    startEdit() {
      this.draftName = this.batch.displayName || "";
      this.draftDescription = this.batch.description || "";
      this.error = "";
      this.editing = true;
    },
    cancel() {
      this.editing = false;
      this.error = "";
    },
    async save() {
      if (this.saving) return;
      this.saving = true;
      this.error = "";
      try {
        const updated = await updateBatchDetails(this.batch, {
          displayName: this.draftName,
          description: this.draftDescription,
        });
        this.editing = false;
        this.$emit("updated", updated);
      } catch (err) {
        // The API's message is written for the distributor (length caps,
        // "can no longer be renamed"); anything else gets a generic line.
        const data = (err && err.response && err.response.data) || {};
        this.error = data.error || "Could not save. Try again.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.bm-details {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid var(--tf-border);
  border-radius: 8px;
  background: var(--tf-surface);
  font-size: 13px;
  line-height: 1.45;
}

.bm-details__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.bm-details__label,
.bm-details__field-label {
  font-size: 10.5px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: var(--tf-fg-quiet);
  font-weight: 500;
}

.bm-details__field-label {
  display: block;
  margin-top: 8px;
}

.bm-details__name {
  font-size: 15px;
  font-weight: 500;
  color: var(--tf-fg-1);
  overflow-wrap: anywhere;
}

/* Line breaks the distributor typed are kept; markup is shown as text. */
.bm-details__desc {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--tf-fg-2);
  margin-top: 2px;
}

.bm-details__quiet,
.bm-details__backoffice {
  color: var(--tf-fg-quiet);
  margin: 6px 0 0;
}

.bm-details__input {
  width: 100%;
  margin-top: 2px;
  padding: 6px 8px;
  border: 1px solid var(--tf-border);
  border-radius: 6px;
  background: #fff;
  font: inherit;
}

.bm-details__textarea {
  resize: vertical;
}

.bm-details__count {
  text-align: right;
  font-size: 11px;
  color: var(--tf-fg-quiet);
}

.bm-details__error {
  color: var(--tf-error);
  margin: 6px 0 0;
}

.bm-details__actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.bm-details__notes {
  margin-top: 4px;
}

.bm-btn {
  border-radius: 9999px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--sm {
  min-height: 36px;
  padding: 6px 14px;
  font-size: 13px;
}

.bm-btn--primary {
  background: var(--tf-primary);
  border-color: var(--tf-primary);
  color: var(--tf-fg-on-primary);
}

.bm-btn__icon {
  font-size: 16px;
}

.bm-btn:disabled {
  cursor: default;
  opacity: 0.7;
}
</style>
